import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  Button,
  Modal,
  Input,
  Form,
  Space,
  message,
  Card,
  Tag,
  Descriptions,
  Row,
  Col,
  Divider,
  Grid,
  DatePicker,
  Popconfirm,
  Badge,
  Statistic,
} from "antd";
import {
  PlusOutlined,
  EyeOutlined,
  PhoneOutlined,
  UserOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ToolOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import api from "../api/Api";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Search } = Input;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

const GestaoTickets = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.lg;

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewVisible, setViewVisible] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [closingId, setClosingId] = useState(null);

  const userStored = JSON.parse(localStorage.getItem("user") || "{}");

  // Filtro por data - default: hoje
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("day"),
    dayjs().endOf("day"),
  ]);

  // ─── API helpers ────────────────────────────────────────────────────────────

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await api.ListTicketMovicel({
        inicio: dateRange[0].format("YYYY-MM-DD HH:mm:ss"),
        fim: dateRange[1].format("YYYY-MM-DD HH:mm:ss"),
      });
      const data = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.tickets)
        ? res.tickets
        : [];
      setTickets(data);
    } catch {
      message.error("Erro ao carregar tickets");
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [dateRange]);

  // ─── Submit novo ticket ─────────────────────────────────────────────────────

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      await api.AddTicketMovicel({
        nome: values.nome,
        operador: values.operador || userStored.nome,
        telefone: values.telefone,
        telefone2: values.telefone2 || "",
        endereco: values.endereco,
        ocorrencia: values.ocorrencia,
      });
      message.success("Ticket criado com sucesso!");
      setModalVisible(false);
      form.resetFields();
      fetchTickets();
    } catch {
      message.error("Erro ao criar ticket");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Fechar ticket ──────────────────────────────────────────────────────────

  const handleFecharTicket = async (id) => {
    setClosingId(id);
    try {
      await api.FecharTicketMovicel({ id });
      message.success("Ticket fechado com sucesso!");
      // Atualizar lista e fechar modal de detalhes se estiver aberto
      if (selectedTicket?.id === id) {
        setViewVisible(false);
        setSelectedTicket(null);
      }
      fetchTickets();
    } catch {
      message.error("Erro ao fechar ticket");
    } finally {
      setClosingId(null);
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
  };

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const getStatusColor = (status) => {
    switch (status) {
      case "ABERTO":
        return "orange";
      case "FECHADO":
        return "green";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "ABERTO":
        return <ClockCircleOutlined />;
      case "FECHADO":
        return <CheckCircleOutlined />;
      default:
        return <ExclamationCircleOutlined />;
    }
  };

  const filteredTickets = useMemo(() => {
    if (!searchTerm) return tickets;
    const term = searchTerm.toLowerCase();
    return tickets.filter(
      (t) =>
        t.nome?.toLowerCase().includes(term) ||
        t.telefone?.includes(term) ||
        t.endereco?.toLowerCase().includes(term) ||
        t.ocorrencia?.toLowerCase().includes(term)
    );
  }, [tickets, searchTerm]);

  // Estatísticas
  const totalAbertos = tickets.filter((t) => t.status === "ABERTO").length;
  const totalFechados = tickets.filter((t) => t.status === "FECHADO").length;

  // ─── Colunas responsivas ────────────────────────────────────────────────────

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: isMobile ? 40 : 60,
      render: (id) => <span style={{ color: "#999", fontSize: 12 }}>#{id}</span>,
    },
    {
      title: "Cliente",
      dataIndex: "nome",
      key: "nome",
      render: (text, record) =>
        isMobile ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <strong>{text}</strong>
            <span style={{ color: "#666", fontSize: 12 }}>
              <EnvironmentOutlined style={{ marginRight: 4 }} />
              {record.endereco}
            </span>
            <Tag
              color={getStatusColor(record.status)}
              icon={getStatusIcon(record.status)}
              style={{ width: "fit-content" }}
            >
              {record.status}
            </Tag>
          </div>
        ) : (
          <div>
            <strong>{text}</strong>
            <div style={{ color: "#999", fontSize: 12 }}>{record.telefone}</div>
          </div>
        ),
    },
    ...(!isMobile
      ? [
          {
            title: "Endereço",
            dataIndex: "endereco",
            key: "endereco",
            render: (v) => (
              <Space>
                <EnvironmentOutlined style={{ color: "#999" }} />
                {v}
              </Space>
            ),
          },
          {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
              <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
                {status}
              </Tag>
            ),
          },
        ]
      : []),
    ...(!isMobile && !isTablet
      ? [
          {
            title: "Operador",
            dataIndex: "operador",
            key: "operador",
            render: (v) => v || "-",
          },
          {
            title: "Downtime",
            dataIndex: "downtime",
            key: "downtime",
            render: (v) => (
              <Space>
                <ClockCircleOutlined style={{ color: "#faad14" }} />
                <span style={{ fontFamily: "monospace" }}>{v || "-"}</span>
              </Space>
            ),
          },
        ]
      : []),
    {
      title: "Criado em",
      dataIndex: "created_at",
      key: "created_at",
      render: (data) => (
        <Space>
          <CalendarOutlined />
          <span style={{ whiteSpace: "nowrap", fontSize: isMobile ? 11 : 13 }}>
            {data ? dayjs(data).format("DD/MM/YY HH:mm") : "-"}
          </span>
        </Space>
      ),
    },
    {
      title: "",
      key: "acoes",
      width: isMobile ? 80 : 160,
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedTicket(record);
              setViewVisible(true);
            }}
            style={{ padding: isMobile ? 0 : undefined }}
          >
            {!isMobile && "Detalhes"}
          </Button>
          {record.status === "ABERTO" && !isMobile && (
            <Popconfirm
              title="Fechar ticket"
              description="Tem a certeza que deseja fechar este ticket?"
              onConfirm={() => handleFecharTicket(record.id)}
              okText="Sim"
              cancelText="Não"
              okButtonProps={{ danger: false }}
            >
              <Button
                type="link"
                danger
                icon={<CloseCircleOutlined />}
                loading={closingId === record.id}
              >
                Fechar
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // ─── Layout helpers ──────────────────────────────────────────────────────────

  const modalWidth = isMobile ? "100%" : isTablet ? 560 : 700;
  const colFull = 24;
  const colHalf = isMobile ? 24 : 12;

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        padding: isMobile ? "12px 8px" : "24px",
        minHeight: "100vh",
        overflowY: "scroll",
        boxSizing: "border-box",
      }}
    >
      {/* Estatísticas */}
      {!isMobile && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Total de Tickets"
                value={tickets.length}
                prefix={<ToolOutlined />}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Em Aberto"
                value={totalAbertos}
                prefix={<ClockCircleOutlined style={{ color: "#faad14" }} />}
                valueStyle={{ color: "#faad14" }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Fechados"
                value={totalFechados}
                prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card
        title={
          <Space>
            <ToolOutlined />
            {isMobile ? "Tickets" : "Gestão de Tickets"}
            {totalAbertos > 0 && (
              <Badge count={totalAbertos} style={{ backgroundColor: "#faad14" }} />
            )}
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
            size={isMobile ? "small" : "middle"}
          >
            {isMobile ? "Novo" : "Novo Ticket"}
          </Button>
        }
        bodyStyle={{ padding: isMobile ? "8px" : "24px" }}
      >
        {/* Filtros */}
        <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={12}>
            <Search
              allowClear
              enterButton={
                <Button type="primary" icon={<SearchOutlined />}>
                  {!isMobile && "Pesquisar"}
                </Button>
              }
              size={isMobile ? "middle" : "large"}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onSearch={(value) => setSearchTerm(value)}
              placeholder="Pesquisar por nome, telefone ou endereço..."
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} md={12}>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates) setDateRange(dates);
              }}
              showTime={{ format: "HH:mm" }}
              format="DD/MM/YYYY HH:mm"
              size={isMobile ? "middle" : "large"}
              style={{ width: "100%" }}
              placeholder={["Data início", "Data fim"]}
            />
          </Col>
        </Row>

        <div style={{ marginBottom: 8, fontSize: 12, color: "#666" }}>
          Mostrando {filteredTickets.length} de {tickets.length} tickets
        </div>

        <Table
          columns={columns}
          dataSource={filteredTickets}
          loading={loading}
          rowKey="id"
          size={isMobile ? "small" : "middle"}
          scroll={{ x: "max-content" }}
          rowClassName={(record) =>
            record.status === "ABERTO" ? "ticket-row-aberto" : ""
          }
          pagination={{
            size: isMobile ? "small" : "default",
            showSizeChanger: !isMobile,
            showTotal: (total, range) =>
              isMobile
                ? `${range[0]}-${range[1]} / ${total}`
                : `${range[0]}-${range[1]} de ${total} tickets`,
          }}
        />
      </Card>

      {/* ── Modal de Criação ─────────────────────────────────────────────────── */}
      <Modal
        title={
          <Space>
            <PlusOutlined />
            Novo Ticket
          </Space>
        }
        open={modalVisible}
        onCancel={handleCancel}
        onOk={() => form.submit()}
        okText="Registrar"
        cancelText="Cancelar"
        width={modalWidth}
        style={isMobile ? { top: 0, margin: 0, maxWidth: "100vw" } : {}}
        styles={
          isMobile
            ? { body: { padding: "12px 8px" }, content: { borderRadius: 0 } }
            : {}
        }
        confirmLoading={submitting}
      >
        <Divider style={{ margin: "8px 0 16px" }} />
        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Row gutter={[12, 0]}>
            {/* Nome */}
            <Col span={colHalf}>
              <Form.Item
                label="Nome do Cliente"
                name="nome"
                rules={[{ required: true, message: "Digite o nome do cliente" }]}
              >
                <Input placeholder="Nome completo" prefix={<UserOutlined />} />
              </Form.Item>
            </Col>

            {/* Operador */}
            <Col span={colHalf}>
              <Form.Item
                label="Operador"
                name="operador"
                initialValue={userStored?.nome}
                rules={[{ required: true, message: "Digite o operador" }]}
              >
                <Input
                  placeholder="Nome do operador"
                  prefix={<UserOutlined />}
                />
              </Form.Item>
            </Col>

            {/* Telefone */}
            <Col span={colHalf}>
              <Form.Item
                label="Telefone"
                name="telefone"
                rules={[{ required: true, message: "Digite o telefone" }]}
              >
                <Input
                  placeholder="Telefone principal"
                  prefix={<PhoneOutlined />}
                  maxLength={15}
                />
              </Form.Item>
            </Col>

            {/* Telefone alternativo */}
            <Col span={colHalf}>
              <Form.Item label="Telefone alternativo" name="telefone2">
                <Input
                  placeholder="Telefone alternativo (opcional)"
                  prefix={<PhoneOutlined />}
                  maxLength={15}
                />
              </Form.Item>
            </Col>

            {/* Endereço */}
            <Col span={colFull}>
              <Form.Item
                label="Endereço"
                name="endereco"
                rules={[{ required: true, message: "Digite o endereço" }]}
              >
                <Input
                  placeholder="Ex: Luanda - Kilamba"
                  prefix={<EnvironmentOutlined />}
                />
              </Form.Item>
            </Col>

            {/* Ocorrência */}
            <Col span={colFull}>
              <Form.Item
                label="Ocorrência"
                name="ocorrencia"
                rules={[
                  { required: true, message: "Descreva a ocorrência" },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Descreva detalhadamente a ocorrência do cliente..."
                  maxLength={1000}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* ── Modal de Visualização ─────────────────────────────────────────────── */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            Detalhes do Ticket #{selectedTicket?.id}
          </Space>
        }
        open={viewVisible}
        onCancel={() => {
          setViewVisible(false);
          setSelectedTicket(null);
        }}
        footer={[
          selectedTicket?.status === "ABERTO" && (
            <Popconfirm
              key="fechar"
              title="Fechar ticket"
              description="Tem a certeza que deseja fechar este ticket?"
              onConfirm={() => handleFecharTicket(selectedTicket.id)}
              okText="Sim"
              cancelText="Não"
            >
              <Button
                danger
                icon={<CloseCircleOutlined />}
                loading={closingId === selectedTicket?.id}
              >
                Fechar Ticket
              </Button>
            </Popconfirm>
          ),
          <Button
            key="close"
            onClick={() => {
              setViewVisible(false);
              setSelectedTicket(null);
            }}
          >
            Fechar
          </Button>,
        ].filter(Boolean)}
        width={isMobile ? "100%" : 600}
        style={isMobile ? { top: 0, margin: 0, maxWidth: "100vw" } : {}}
        styles={
          isMobile
            ? { body: { padding: "12px 8px" }, content: { borderRadius: 0 } }
            : {}
        }
      >
        {selectedTicket && (
          <Descriptions
            column={1}
            bordered
            size={isMobile ? "small" : "default"}
            labelStyle={isMobile ? { width: "38%" } : {}}
          >
            <Descriptions.Item label="Status">
              <Tag
                color={getStatusColor(selectedTicket.status)}
                icon={getStatusIcon(selectedTicket.status)}
              >
                {selectedTicket.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Nome">
              <Space>
                <UserOutlined />
                {selectedTicket.nome}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Operador">
              <Space>
                <UserOutlined />
                {selectedTicket.operador || "-"}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Telefone">
              <Space>
                <PhoneOutlined />
                {selectedTicket.telefone}
              </Space>
            </Descriptions.Item>
            {selectedTicket.telefone2 && (
              <Descriptions.Item label="Telefone 2">
                <Space>
                  <PhoneOutlined />
                  {selectedTicket.telefone2}
                </Space>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Endereço">
              <Space>
                <EnvironmentOutlined />
                {selectedTicket.endereco}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Ocorrência">
              {selectedTicket.ocorrencia}
            </Descriptions.Item>
            <Descriptions.Item label="Downtime">
              <Space>
                <ClockCircleOutlined style={{ color: "#faad14" }} />
                <span style={{ fontFamily: "monospace" }}>
                  {selectedTicket.downtime || "-"}
                </span>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Início">
              <Space>
                <CalendarOutlined />
                {selectedTicket.inicio
                  ? dayjs(selectedTicket.inicio).format("DD/MM/YYYY HH:mm:ss")
                  : "-"}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Fim">
              <Space>
                <CalendarOutlined />
                {selectedTicket.fim
                  ? dayjs(selectedTicket.fim).format("DD/MM/YYYY HH:mm:ss")
                  : "-"}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Criado em">
              <Space>
                <CalendarOutlined />
                {selectedTicket.created_at
                  ? dayjs(selectedTicket.created_at).format(
                      "DD/MM/YYYY HH:mm:ss"
                    )
                  : "-"}
              </Space>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <style>{`
        .ticket-row-aberto {
          background-color: #fffbe6 !important;
        }
        .ticket-row-aberto:hover > td {
          background-color: #fff7cc !important;
        }
      `}</style>
    </div>
  );
};

export default GestaoTickets;